import React, { useEffect, useMemo, useState } from 'react';
import { Authenticator } from '@aws-amplify/ui-react';
import { getCurrentUser } from '@aws-amplify/auth';
import { generateClient } from 'aws-amplify/data';
import { LayoutDashboard, Hammer, Building2, Users, LogOut, Upload, Settings, Eye, Wallet } from 'lucide-react';
import '@aws-amplify/ui-react/styles.css';
import './App.css';
import logo from './logo.png';
import outputs from './amplify_outputs.json';
import { INITIAL_STATE } from './data/mockData';
import { Toast, MediaPreviewModal } from './components/SharedUI';
import {
  ClientDashboard,
  ClientConstruction,
  ClientFinancials,
  ClientMedia,
  ClientChat,
  AdminProjects,
  AdminClients,
  AdminMedia
} from './screens/Views';

const hasAPI = !!outputs?.API?.GraphQL?.endpoint;
const client = hasAPI ? generateClient({ authMode: 'userPool' }) : null;
const defaultXeroEndpoint = process.env.REACT_APP_XERO_SYNC_URL || 'https://hiw7vzi4mkn42u4ycs6uzr7ifi0hizob.lambda-url.ap-southeast-2.on.aws/';

export default function App() {
  const [activeTab, setActiveTab] = useState('projects');
  const [appState, setAppState] = useState(INITIAL_STATE);
  const [toast, setToast] = useState({ visible: false, message: '', type: 'success' });
  const [previewMedia, setPreviewMedia] = useState(null);
  const [impersonatedProjectId, setImpersonatedProjectId] = useState(null);
  const [liveProjects, setLiveProjects] = useState([]);
  const [showAPINotice, setShowAPINotice] = useState(!hasAPI);
  const [userEmail, setUserEmail] = useState(null);
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [loginMode, setLoginMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.localStorage.getItem('mastaLoginMode') || 'client';
    }
    return 'client';
  });
  const [xeroSyncing, setXeroSyncing] = useState(false);
  const [xeroEndpoint, setXeroEndpoint] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.localStorage.getItem('xeroEndpointUrl') || defaultXeroEndpoint;
    }
    return defaultXeroEndpoint;
  });
  const [xeroEndpointInput, setXeroEndpointInput] = useState('');
  const [xeroEndpointStatus, setXeroEndpointStatus] = useState('idle');

  useEffect(() => {
    setXeroEndpointInput(xeroEndpoint);
  }, [xeroEndpoint]);

  useEffect(() => {
    async function fetchAWSProjects() {
      if (!client?.models?.Project?.list) {
        console.info('AWS GraphQL API not configured; using mock portal data.');
        return;
      }

      try {
        const authUser = await getCurrentUser();
        const email = authUser?.attributes?.email || authUser?.username || authUser?.signInUserSession?.accessToken?.payload?.email || authUser?.signInUserSession?.idToken?.payload?.email;
        setUserEmail(email);

        const groups = authUser?.signInUserSession?.accessToken?.payload?.['cognito:groups'] || [];
        const localDevAdmin = typeof window !== 'undefined' && window.location.hostname === 'localhost' && loginMode === 'builder';
        const admin = groups.includes('Builders') || groups.includes('Admin') || String(email || '').toLowerCase().includes('builder') || localDevAdmin;
        setIsAdminUser(admin);
      } catch (err) {
        console.warn('Unable to load AWS projects:', err);
      }
    }
    fetchAWSProjects();
  }, [loginMode]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.localStorage.setItem('mastaLoginMode', loginMode);
    }
  }, [loginMode]);

  const showToast = (message, type = 'success') => {
    setToast({ visible: true, message, type });
    setTimeout(() => setToast({ visible: false, message: '', type: 'success' }), 3000);
  };

  const handleSaveXeroEndpoint = (e) => {
    e.preventDefault();
    const normalized = xeroEndpointInput.trim();
    if (typeof window !== 'undefined') {
      if (normalized) {
        window.localStorage.setItem('xeroEndpointUrl', normalized);
      } else {
        window.localStorage.removeItem('xeroEndpointUrl');
      }
    }
    setXeroEndpoint(normalized || defaultXeroEndpoint);
    setXeroEndpointInput(normalized);
    setXeroEndpointStatus('idle');
    showToast('Xero endpoint saved locally.', 'success');
  };

  const handleClearXeroEndpoint = () => {
    if (typeof window !== 'undefined') {
      window.localStorage.removeItem('xeroEndpointUrl');
    }
    setXeroEndpoint(defaultXeroEndpoint);
    setXeroEndpointInput('');
    setXeroEndpointStatus('idle');
    showToast('Xero endpoint override cleared.', 'success');
  };

  const handleForceXeroSync = async () => {
    const endpoint = xeroEndpoint || defaultXeroEndpoint;
    if (!endpoint) {
      showToast('Xero sync URL is not configured. Set or save an endpoint in Settings.', 'error');
      return;
    }

    setXeroSyncing(true);
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action: 'syncProjects' }),
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result?.message || response.statusText || 'Xero sync failed');
      }

      showToast('Xero sync completed successfully.', 'success');
      console.log('Xero sync response:', result);
    } catch (err) {
      console.error('Xero sync failed:', err);
      showToast(`Xero sync failed: ${err.message || err}`, 'error');
    } finally {
      setXeroSyncing(false);
    }
  };

  const handleTestXeroEndpoint = async () => {
    const endpoint = xeroEndpoint || defaultXeroEndpoint;
    if (!endpoint) {
      showToast('No Xero endpoint available to test. Please configure one first.', 'error');
      return;
    }

    setXeroSyncing(true);
    setXeroEndpointStatus('testing');
    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ action: 'ping' }),
      });

      const result = await response.json();
      if (!response.ok) {
        throw new Error(result?.message || response.statusText || 'Endpoint test failed');
      }

      setXeroEndpointStatus('success');
      showToast('Xero endpoint responded successfully.', 'success');
      console.log('Xero endpoint ping result:', result);
    } catch (err) {
      setXeroEndpointStatus('error');
      console.error('Xero endpoint test failed:', err);
      showToast(`Endpoint test failed: ${err.message || err}`, 'error');
    } finally {
      setXeroSyncing(false);
    }
  };

  const handleImpersonate = (projectId) => {
    setImpersonatedProjectId(projectId);
    setActiveTab('dashboard');
  };

  const handleExitImpersonation = () => {
    setImpersonatedProjectId(null);
    setActiveTab('projects');
  };

  const authComponents = {
    Header() {
      return (
        <div className="text-center mb-6">
          <img src={logo} alt="Masta Homes" className="masta-logo mx-auto" />
          <div className="flex justify-center gap-3 mt-4">
            <button
              type="button"
              onClick={() => setLoginMode('builder')}
              className={`rounded-full px-4 py-2 font-semibold transition ${loginMode === 'builder' ? 'bg-red-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
            >
              Builder login
            </button>
            <button
              type="button"
              onClick={() => setLoginMode('client')}
              className={`rounded-full px-4 py-2 font-semibold transition ${loginMode === 'client' ? 'bg-red-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200'}`}
            >
              Client login
            </button>
          </div>
          <p className="text-sm text-slate-500 mt-3">
            {loginMode === 'builder'
              ? 'Use your builder/admin credentials to open the admin portal.'
              : 'Use your client credentials to open the client portal.'}
          </p>
        </div>
      );
    }
  };

  const fallbackProjects = Object.values(appState.projects).filter((project) => {
    if (!userEmail) return true;
    return isAdminUser || String(project.clientEmail).toLowerCase() === String(userEmail).toLowerCase();
  });

  const projectList = liveProjects.length > 0 ? liveProjects : fallbackProjects;
  const activeProject = impersonatedProjectId
    ? projectList.find((p) => p.id === impersonatedProjectId) || projectList[0]
    : projectList[0];

  return (
    <div className="portal-container">
      <Authenticator hideSignUp={true} components={authComponents}>
        {({ signOut, user }) => {
          const groups = user?.signInUserSession?.accessToken?.payload['cognito:groups'] || [];
          const localDevAdmin = typeof window !== 'undefined' && window.location.hostname === 'localhost' && loginMode === 'builder';
          const isAdmin = groups.includes('Builders') || groups.includes('Admin') || user?.username?.toLowerCase().includes('builder') || localDevAdmin;
          const isClientView = !isAdmin || impersonatedProjectId !== null;

          const navItems = isClientView
            ? [
                { id: 'dashboard', icon: LayoutDashboard, label: 'Overview' },
                { id: 'financial', icon: Wallet, label: 'Financials' },
                { id: 'construction', icon: Hammer, label: 'Construction Stage' },
                { id: 'media', icon: Upload, label: 'Files & Media' },
                { id: 'chat', icon: Users, label: 'Messages' }
              ]
            : [
                { id: 'projects', icon: Building2, label: 'Builder Projects' },
                { id: 'clients', icon: Users, label: 'Client Management' },
                { id: 'adminMedia', icon: Upload, label: 'Site Media & Docs' },
                { id: 'settings', icon: Settings, label: 'Settings' }
              ];

          const normalizedTab = navItems.some((item) => item.id === activeTab) ? activeTab : navItems[0].id;
          const adminProjectList = liveProjects.length > 0 ? liveProjects : Object.values(appState.projects);

          return (
            <div className="fixed inset-0 flex bg-slate-50 font-sans text-slate-800 overflow-hidden z-50">
              <aside className="w-64 bg-slate-900 text-white flex flex-col flex-shrink-0 z-20">
                <div className="p-6 border-b border-slate-800 flex justify-center bg-slate-900/50">
                  <div className="text-2xl font-black text-red-500 tracking-tighter">MASTA HOMES</div>
                </div>
                <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
                  <div className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                    {isClientView ? 'Project Portal' : 'Builder Admin'}
                  </div>
                  {navItems.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => setActiveTab(item.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-all ${normalizedTab === item.id ? 'bg-red-600 text-white shadow-sm' : 'text-slate-400 hover:bg-slate-800 hover:text-white'}`}
                    >
                      <item.icon size={20} /> <span className="font-medium">{item.label}</span>
                    </button>
                  ))}
                </nav>
                <div className="p-4 border-t border-slate-800 bg-slate-900">
                  <button onClick={signOut} className="w-full flex items-center gap-3 px-4 py-3 text-red-400 hover:bg-red-500/10 rounded-xl transition-colors">
                    <LogOut size={20} /> <span className="font-medium">Logout</span>
                  </button>
                </div>
              </aside>

              <main className="flex-1 overflow-y-auto p-4 md:p-8">
                {!hasAPI && showAPINotice && (
                  <div className="mb-4 p-4 rounded-xl bg-yellow-50 border border-yellow-300 text-yellow-900">
                    <strong>No GraphQL API configured:</strong> running in mock mode. Run <span className="font-mono">amplify push</span> to provision the backend API.
                    <button onClick={() => setShowAPINotice(false)} className="ml-4 underline">Dismiss</button>
                  </div>
                )}
                {isClientView && normalizedTab === 'dashboard' && <ClientDashboard project={activeProject} />}
                {isClientView && normalizedTab === 'financial' && <ClientFinancials project={activeProject} />}
                {isClientView && normalizedTab === 'construction' && <ClientConstruction project={activeProject} />}
                {isClientView && normalizedTab === 'media' && <ClientMedia project={activeProject} onPreview={setPreviewMedia} showToast={showToast} />}
                {isClientView && normalizedTab === 'chat' && <ClientChat projectId={activeProject?.id} />}

                {!isClientView && normalizedTab === 'projects' && (
                  <AdminProjects projects={adminProjectList} onImpersonate={handleImpersonate} showToast={showToast} />
                )}
                {!isClientView && normalizedTab === 'clients' && <AdminClients showToast={showToast} />}
                {!isClientView && normalizedTab === 'adminMedia' && <AdminMedia projects={adminProjectList} showToast={showToast} />}
                {!isClientView && normalizedTab === 'settings' && (
                  <div className="animate-[fadeIn_0.3s_ease-out]">
                    <h2 className="text-2xl font-bold text-slate-800 mb-6">Global Settings</h2>
                    <div className="bg-white shadow-sm border border-slate-200 rounded-3xl p-6 max-w-3xl">
                      <p className="text-slate-600 mb-4">Connected AWS Amplify environment: <strong>ap-southeast-2</strong></p>
                      <div className="grid gap-4">
                        <div className="text-sm text-slate-600">
                          <div className="font-semibold text-slate-800 mb-1">Build-time Xero endpoint</div>
                          <code className="block rounded-xl bg-slate-100 p-3 text-xs text-slate-700 break-words">{defaultXeroEndpoint || '(none)'}</code>
                        </div>
                        <div className="text-sm text-slate-600">
                          <div className="font-semibold text-slate-800 mb-1">Local runtime override</div>
                          <code className="block rounded-xl bg-slate-100 p-3 text-xs text-slate-700 break-words">{xeroEndpoint || '(none)'}</code>
                        </div>
                        <div className="text-sm text-slate-600">
                          <div className="flex items-center justify-between font-semibold text-slate-800 mb-1">
                            <span>Effective endpoint</span>
                            {xeroEndpointStatus !== 'idle' && (
                              <span className={`rounded-full px-3 py-1 text-[11px] font-semibold ${
                                xeroEndpointStatus === 'success' ? 'bg-emerald-100 text-emerald-700' : xeroEndpointStatus === 'error' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'
                              }`}>
                                {xeroEndpointStatus === 'success'
                                  ? 'Verified'
                                  : xeroEndpointStatus === 'error'
                                  ? 'Failed'
                                  : 'Testing...'}
                              </span>
                            )}
                          </div>
                          <code className="block rounded-xl bg-slate-100 p-3 text-xs text-slate-700 break-words">{xeroEndpoint || defaultXeroEndpoint || '(not configured)'}</code>
                        </div>
                        <form onSubmit={handleSaveXeroEndpoint} className="grid gap-3">
                          <label className="text-sm font-semibold text-slate-700">Override Xero endpoint</label>
                          <div className="flex gap-2 flex-col sm:flex-row">
                            <input
                              value={xeroEndpointInput}
                              onChange={(e) => setXeroEndpointInput(e.target.value)}
                              placeholder="https://example.execute-api.region.amazonaws.com/prod"
                              className="w-full border border-slate-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 bg-slate-50"
                            />
                            <button
                              type="submit"
                              className="bg-red-600 hover:bg-red-700 text-white px-5 py-3 rounded-xl font-semibold transition-colors"
                            >
                              Save override
                            </button>
                          </div>
                          <button
                            type="button"
                            onClick={handleClearXeroEndpoint}
                            className="text-sm text-slate-700 underline hover:text-slate-900 self-start"
                          >
                            Clear override
                          </button>
                        </form>
                        <div className="border-t border-slate-200 pt-4 space-y-3">
                          <p className="text-slate-600 mb-4">
                            {xeroEndpoint || defaultXeroEndpoint
                              ? 'Live Xero sync is prepared via Lambda integration.'
                              : 'Xero Lambda sync is not configured. Set or save an endpoint to enable live sync.'}
                          </p>
                          <div className="flex flex-col sm:flex-row gap-3">
                            <button
                              onClick={handleForceXeroSync}
                              disabled={xeroSyncing || !(xeroEndpoint || defaultXeroEndpoint)}
                              className={`flex-1 bg-slate-900 hover:bg-slate-800 text-white px-5 py-3 rounded-xl font-semibold transition-colors ${xeroSyncing || !(xeroEndpoint || defaultXeroEndpoint) ? 'opacity-70 cursor-not-allowed' : ''}`}
                            >
                              {xeroSyncing ? 'Syncing Xero...' : 'Force Xero Sync'}
                            </button>
                            <button
                              onClick={handleTestXeroEndpoint}
                              disabled={xeroSyncing || !(xeroEndpoint || defaultXeroEndpoint)}
                              className={`flex-1 border border-slate-300 hover:border-slate-900 text-slate-900 px-5 py-3 rounded-xl font-semibold transition-colors ${xeroSyncing || !(xeroEndpoint || defaultXeroEndpoint) ? 'opacity-70 cursor-not-allowed' : ''}`}
                            >
                              {xeroSyncing ? 'Testing...' : 'Test Endpoint'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </main>

              {impersonatedProjectId && (
                <div className="fixed top-0 left-64 right-0 bg-slate-800 text-white px-6 py-3 flex justify-between items-center z-30">
                  <div className="text-sm">
                    Viewing portal as <strong>{activeProject?.name}</strong>
                  </div>
                  <button onClick={handleExitImpersonation} className="bg-slate-700 hover:bg-slate-600 rounded-xl px-4 py-2 text-sm">
                    Exit Portal View
                  </button>
                </div>
              )}
            </div>
          );
        }}
      </Authenticator>

      <Toast visible={toast.visible} message={toast.message} type={toast.type} />
      <MediaPreviewModal item={previewMedia} onClose={() => setPreviewMedia(null)} />
    </div>
  );
}
